import type {
  MarketplaceFieldMapping,
  MarketplaceTemplateDataResponse,
} from '../types'
import { camelizeSalesforceReferralTemplate } from './camelizeSalesforceReferralTemplate'
import { snakeizeMarketplaceReferralTemplate } from './snakeizeMarketplaceReferralTemplate'
import { azureMarketplaceFields } from './azureMarketplaceFields'

type MappingServiceField = MarketplaceFieldMapping & {
  crmField?: string
  crmFieldLabel?: string
  crmFieldType?: string
  crmObject?: string
}

const splitHubspotCrmField = (field: MappingServiceField) => {
  const crmField = field.crmField ?? ''
  const separatorIndex = crmField.indexOf('.')
  const crmObject =
    field.crmObject ??
    field.hubspotObject ??
    (separatorIndex >= 0 ? crmField.slice(0, separatorIndex) : '')

  return {
    hubspotObject: crmObject,
    hubspotField:
      separatorIndex >= 0 ? crmField.slice(separatorIndex + 1) : crmField,
  }
}

// The mapping service stores nested field groups; the existing HubSpot editor
// reads and updates top-level rows. Keep both representations in sync at the API boundary.
export const readAzureHubspotTemplate = (data: unknown) => {
  const template = camelizeSalesforceReferralTemplate(
    data
  ) as MarketplaceTemplateDataResponse
  const fields: Record<string, MappingServiceField> = {
    ...(template.fieldMappings.fields?.fieldsMapping ?? {}),
  }
  const stakeholdersFieldKey = 'Labra__Stakeholders__c'
  if (!fields[stakeholdersFieldKey]) {
    fields[stakeholdersFieldKey] = {
      referralField: stakeholdersFieldKey,
      referralFieldType: 'Boolean',
      hubspotObject: '',
      hubspotField: '',
      hubspotFieldLabel: '',
      hubspotFieldType: '',
      crmField: '',
      crmFieldLabel: '',
      crmFieldType: '',
      isHidden: false,
      isReadonly: false,
      isMapped: false,
      label: 'Stakeholders',
      required: false,
      displayOrder: 10,
      defaultValue: '',
      category: 'Metadata',
      picklist: null,
      picklistMappings: null,
    }
  }
  const rows = Object.fromEntries(
    Object.entries(fields).map(([key, fieldValue]) => {
      const field = fieldValue
      const display = azureMarketplaceFields[key]
      const { hubspotObject, hubspotField } = splitHubspotCrmField(field)
      return [
        key,
        {
          ...field,
          label: display?.label ?? field.label,
          referralFieldType:
            display?.fieldType ??
            (field.referralFieldType === '.txt/.pdf'
              ? 'File'
              : field.referralFieldType),
          displayOrder: display?.displayOrder ?? field.displayOrder,
          hubspotObject,
          hubspotField,
          hubspotFieldLabel: field.crmFieldLabel ?? '',
          hubspotFieldType: field.crmFieldType ?? '',
          category: display?.category ?? 'Fields',
        },
      ]
    })
  )
  return {
    ...template,
    useTemplateFromMappingService: true,
    fieldMappings: {
      ...template.fieldMappings,
      fields: {
        ...template.fieldMappings.fields,
        fieldsMapping: fields,
      },
      ...rows,
    },
  }
}

export const writeAzureHubspotTemplate = (
  template: MarketplaceTemplateDataResponse
) => {
  const fields = template.fieldMappings.fields?.fieldsMapping ?? {}
  const fieldsMapping = Object.fromEntries(
    Object.entries(fields).map(([key, fieldValue]) => {
      const original = fieldValue as MappingServiceField
      const edited = template.fieldMappings[
        key as keyof typeof template.fieldMappings
      ] as MarketplaceFieldMapping | undefined
      const hubspotField = edited?.hubspotField ?? original.crmField ?? ''
      const hubspotObject =
        edited?.hubspotObject ?? original.crmObject ?? original.hubspotObject
      const crmField =
        hubspotObject && hubspotField && !hubspotField.includes('.')
          ? `${hubspotObject}.${hubspotField}`
          : hubspotField
      return [
        key,
        {
          ...original,
          ...edited,
          label: original.label,
          referralFieldType: original.referralFieldType,
          crmField,
          crmFieldLabel: edited?.hubspotFieldLabel ?? original.crmFieldLabel,
          crmFieldType: edited?.hubspotFieldType ?? original.crmFieldType,
        },
      ]
    })
  )
  return snakeizeMarketplaceReferralTemplate({
    ...template,
    fieldMappings: {
      ...template.fieldMappings,
      fields: { fieldsMapping },
    },
  })
}
